/* eslint-disable react-refresh/only-export-components */
import {
  DimeMenubar,
  DimeMenubarMenu,
  DimeMenubarTrigger,
  DimeMenubarContent,
  DimeMenubarItem,
  DimeMenubarSeparator,
  DimeMenubarShortcut,
  DimeMenubarCheckboxItem,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/menubar')({
  component: MenubarDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function BasicMenubar() {
  const [showGrid, setShowGrid] = React.useState(true);

  return (
    <DimeMenubar>
      <DimeMenubarMenu>
        <DimeMenubarTrigger>File</DimeMenubarTrigger>
        <DimeMenubarContent>
          <DimeMenubarItem>
            New part
            <DimeMenubarShortcut>⌘N</DimeMenubarShortcut>
          </DimeMenubarItem>
          <DimeMenubarItem>Open…</DimeMenubarItem>
          <DimeMenubarSeparator />
          <DimeMenubarItem variant="destructive">Delete</DimeMenubarItem>
        </DimeMenubarContent>
      </DimeMenubarMenu>
      <DimeMenubarMenu>
        <DimeMenubarTrigger>Edit</DimeMenubarTrigger>
        <DimeMenubarContent>
          <DimeMenubarItem>Undo</DimeMenubarItem>
          <DimeMenubarItem>Redo</DimeMenubarItem>
        </DimeMenubarContent>
      </DimeMenubarMenu>
      <DimeMenubarMenu>
        <DimeMenubarTrigger>View</DimeMenubarTrigger>
        <DimeMenubarContent>
          <DimeMenubarCheckboxItem
            checked={showGrid}
            onCheckedChange={(val) => setShowGrid(!!val)}
            onSelect={(event) => event.preventDefault()}
          >
            Show grid lines
          </DimeMenubarCheckboxItem>
        </DimeMenubarContent>
      </DimeMenubarMenu>
    </DimeMenubar>
  );
}

function MenubarDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Menubar</h1>
      <p className="mb-8 text-muted-foreground">
        A persistent horizontal bar of dropdown menus, like a desktop application's menu. Each{' '}
        <code className="font-mono text-sm">DimeMenubarTrigger</code> must live inside a{' '}
        <code className="font-mono text-sm">DimeMenubarMenu</code>.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicMenubar />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Group items with <code className="font-mono">DimeMenubarSeparator</code>, add hints with{' '}
          <code className="font-mono">DimeMenubarShortcut</code>, mark dangerous actions with{' '}
          <code className="font-mono">variant="destructive"</code>, and toggle state with{' '}
          <code className="font-mono">DimeMenubarCheckboxItem</code>.
        </p>
      </DocsSection>
    </div>
  );
}
